import Papa from "papaparse";
import { ParsedProduct } from "../types";
import { REQUIRED_CSV_HEADERS, TEXT } from "../constants";

export interface CsvParseResult {
  data: ParsedProduct[];
  errors: string[];
  isValid: boolean;
}

/**
 * Validates required headers exist in CSV
 */
const validateHeaders = (headers: string[]): string[] => {
  const errors: string[] = [];

  for (const required of REQUIRED_CSV_HEADERS) {
    if (!headers.includes(required)) {
      errors.push(`Missing required header: ${required}`);
    }
  }

  return errors;
};

/**
 * Validates row data has required values
 */
const validateRow = (row: ParsedProduct, rowIndex: number): string[] => {
  const errors: string[] = [];

  if (!row.product_name || row.product_name.trim() === "") {
    errors.push(`Row ${rowIndex + 1}: Missing product_name value`);
  }

  if (!row.product_id || row.product_id.trim() === "") {
    errors.push(`Row ${rowIndex + 1}: Missing product_id value`);
  }

  return errors;
};

/**
 * Parses a CSV file containing product data
 * @param file - The CSV file to parse
 * @param customLimit - Optional custom product limit. If undefined, no limit is enforced.
 * @returns A promise resolving to the parse result
 */
export const parseProductCsv = (
  file: File,
  customLimit?: number
): Promise<CsvParseResult> => {
  return new Promise((resolve) => {
    Papa.parse<ParsedProduct>(file, {
      header: true,
      skipEmptyLines: true,
      transformHeader: (header) => header.trim().toLowerCase(),
      complete: (results) => {
        const headerErrors = validateHeaders(
          Object.keys(results.data[0] || {})
        );

        // Validate all rows for required data
        const rowErrors: string[] = [];

        for (let index = 0; index < results.data.length; index++) {
          const row = results.data[index];
          rowErrors.push(...validateRow(row, index));
        }

        // Check if we have too many rows (only if limit is defined)
        const tooManyRows =
          customLimit !== undefined && results.data.length > customLimit
            ? [TEXT.ERROR_EXCEED_LIMIT(results.data.length, customLimit)]
            : [];

        // Combine all errors
        const allErrors = [
          ...headerErrors,
          ...rowErrors,
          ...tooManyRows,
          ...results.errors.map(
            (err) => `Parse error: ${err.message} at row ${err.row}`
          ),
        ];

        resolve({
          data: results.data,
          errors: allErrors,
          isValid: allErrors.length === 0,
        });
      },
      error: (error) => {
        resolve({
          data: [],
          errors: [`Failed to parse CSV: ${error.message}`],
          isValid: false,
        });
      },
    });
  });
};
